import { Stack } from '@mui/material';
import { ROUTES } from 'config';
import { Modal, SelectInput, TextField } from 'melp-design/components';
import { FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useAdminCreateBenefitTemplate } from 'store/admin-benefit-templates';
import { benefitCategories, BenefitCategory } from 'store/benefits';
import { ModalProps } from 'store/modal';
import { routerHistory } from 'utils/router-history';
import { usePredefinedToasts } from 'utils/Toast';

interface Values {
  name: string;
  category: BenefitCategory;
}

interface Props extends ModalProps {}

export const CreateBenefitTemplateModal: FC<Props> = ({ closeModal }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { handleSubmit, control } = useForm<Values>({
    defaultValues: {
      name: '',
      category: benefitCategories[0],
    },
  });

  const { mutate: createTemplate, isLoading: isCreating } =
    useAdminCreateBenefitTemplate();

  const onSubmit: SubmitHandler<Values> = (values) => {
    createTemplate(
      { ...values },
      {
        onSuccess: ({ data }) => {
          predefinedToasts.success.added();
          closeModal();
          routerHistory.push(
            ROUTES.admin.benefitTemplates.details.replace(':id', data.id),
          );
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('benefitTemplates.newTemplate')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.create'),
            type: 'submit',
            disabled: isCreating,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('benefitTemplates.templateName')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="category"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <SelectInput
                label={t('marketplaceItems.benefitPlanTypes')}
                name={name}
                value={value}
                onChange={onChange}
                options={benefitCategories.map((category) => ({
                  value: category,
                  label: t(`menu.${category}`),
                }))}
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
